import { LuPlus, LuTrash2 } from "react-icons/lu";
import RadioButtonControlGroup from "../../../../../shared/RadioButtonControlGroup";
import TextField from "../../../../common/TextField";
import CentivoNetworkSeries from "./CentivoNetworkSeries";
import ExternalNetworkFields from "./ExternalNetworkFields";
import {
    EXTERNAL_DIRECTORY_PRE_ENROLLMENT_HELPER,
    NetworkDirectoryType,
    resolveExternalNeedsVpc,
    resolveNetworkSeriesVisibility,
} from "../../../../../../utils/planManager/networkConfigurationUtils.js";
import "./RegisterNetwork.scss";

/**
 * Directory-type radio options with per-option helper text (Networks tab).
 * @readonly
 */
const DIRECTORY_TYPE_OPTIONS = Object.freeze([
    {
        value: NetworkDirectoryType.CENTIVO,
        label: "Centivo",
        helper: "Requires the Find Care directory search",
    },
    {
        value: NetworkDirectoryType.EXTERNAL,
        label: "External/RBP",
        helper: "Requires a link to a third party website or an RBP plan",
    },
    {
        value: NetworkDirectoryType.MIXED,
        label: "Centivo + External/RBP",
        helper:
            "Requires the Find Care directory search as well as link to a third party website",
    },
]);

const INCLUDE_PRE_ENROLLMENT_OPTIONS = [
    { value: "include", label: "Include" },
    { value: "exclude", label: "Exclude" },
];

/**
 * One network configuration block within the Networks tab: network name,
 * directory type, pre-enrollment inclusion, and the directory-driven Centivo /
 * External subforms, plus delete and add controls. Presentational — every
 * change flows up via callbacks; owns no state.
 *
 * @param {object} props
 * @param {number} props.index - Zero-based row position (shown as `index + 1`).
 * @param {Object} props.config - This row's network configuration.
 * @param {Record<string, Array<string>>} props.seriesOptions - Centivo series dropdown options.
 * @param {boolean} props.canDelete - Whether the delete control is shown.
 * @param {boolean} props.showAddButton - Whether the add-network control is shown.
 * @param {(index: number, field: string, value: *) => void} props.onFieldChange
 * @param {(index: number, directoryType: string) => void} props.onDirectoryTypeChange
 * @param {(index: number, include: boolean) => void} props.onIncludePreEnrollmentChange
 * @param {(index: number, seriesIndex: number, code: string) => void} props.onSeriesChange
 * @param {(index: number, code: string) => void} props.onPreferredChange
 * @param {(index: number, field: string, value: *) => void} props.onExternalFieldChange
 * @param {(index: number) => void} props.onDeleteClick
 * @param {() => void} props.onAddClick
 * @returns {import('react').JSX.Element}
 */
const RegisterNetwork = ({
    index,
    config,
    seriesOptions,
    canDelete,
    showAddButton,
    onFieldChange,
    onDirectoryTypeChange,
    onIncludePreEnrollmentChange,
    onSeriesChange,
    onPreferredChange,
    onExternalFieldChange,
    onDeleteClick,
    onAddClick,
}) => {
    const { showCentivo, showExternal } = resolveNetworkSeriesVisibility(
        config.directoryType,
    );
    const needsVpc = resolveExternalNeedsVpc(
        config.directoryType,
        config.thirdPartyNetwork,
    );
    const isExternalOnly =
        config.directoryType === NetworkDirectoryType.EXTERNAL;
    const includeValue =
        config.includePreEnrollmentSite === true
            ? "include"
            : config.includePreEnrollmentSite === false
              ? "exclude"
              : null;

    return (
        <div className="config-register-network">
            <div className="config-register-network__header">
                <span className="config-register-network__index">
                    {index + 1}
                </span>
                <p>
                    Enter a network name and select a directory type to choose
                    the next options.
                </p>
            </div>

            <TextField
                id={`network-name-${index}`}
                label="Network name"
                placeholder="Enter network name"
                value={config.name}
                onChange={(value) => onFieldChange(index, "name", value)}
            />

            <fieldset className="config-radio-group">
                <legend className="config-field__label">Directory type</legend>
                <p className="config-field__helper">
                    Select what type of directory search this network will use.
                </p>
                {DIRECTORY_TYPE_OPTIONS.map((option) => (
                    <label className="config-radio" key={option.value}>
                        <input
                            type="radio"
                            name={`directory-type-${index}`}
                            value={option.value}
                            checked={config.directoryType === option.value}
                            onChange={() =>
                                onDirectoryTypeChange(index, option.value)
                            }
                        />
                        <span className="config-radio__label">
                            {option.label}
                            <span className="config-radio__helper">
                                {option.helper}
                            </span>
                        </span>
                    </label>
                ))}
            </fieldset>

            <div className="config-field">
                <span className="config-field__label">Pre-enrollment site</span>
                <p className="config-field__helper">
                    Select to include or exclude this network from the
                    pre-enrollment site.
                </p>
                <RadioButtonControlGroup
                    ariaLabel={`Pre-enrollment site inclusion for network ${index + 1}`}
                    value={includeValue}
                    onChange={(value) =>
                        onIncludePreEnrollmentChange(index, value === "include")
                    }
                    options={INCLUDE_PRE_ENROLLMENT_OPTIONS.map((option) => ({
                        ...option,
                        disabled: option.value === "include" && isExternalOnly,
                        tooltip:
                            option.value === "include" && isExternalOnly
                                ? EXTERNAL_DIRECTORY_PRE_ENROLLMENT_HELPER
                                : undefined,
                        label: option.label,
                    }))}
                />
                {isExternalOnly && (
                    <p className="config-field__helper config-field__helper--info">
                        {EXTERNAL_DIRECTORY_PRE_ENROLLMENT_HELPER}
                    </p>
                )}
            </div>

            {showCentivo ? (
                <CentivoNetworkSeries
                    seriesOptions={seriesOptions}
                    networkSeries={config.networkSeries}
                    preferredNetworkCodes={config.preferredNetworkCodes}
                    onSeriesChange={(seriesIndex, code) =>
                        onSeriesChange(index, seriesIndex, code)
                    }
                    onPreferredChange={(code) => onPreferredChange(index, code)}
                />
            ) : null}

            {showExternal ? (
                <ExternalNetworkFields
                    rbpAdministrator={config.rbpAdministrator}
                    thirdPartyNetwork={config.thirdPartyNetwork}
                    requireVpcModule={config.requireVpcModule}
                    needsVpc={needsVpc}
                    onFieldChange={(field, value) =>
                        onExternalFieldChange(index, field, value)
                    }
                />
            ) : null}

            <div className="config-register-network__actions">
                {canDelete ? (
                    <button
                        type="button"
                        className="config-register-network__delete"
                        onClick={() => onDeleteClick(index)}
                    >
                        Delete network
                        <LuTrash2 aria-hidden size={16} />
                    </button>
                ) : null}
            </div>

            {showAddButton ? (
                <button
                    type="button"
                    className="config-register-network__add"
                    onClick={onAddClick}
                >
                    Add a network
                    <LuPlus aria-hidden size={16} />
                </button>
            ) : null}
        </div>
    );
};

export default RegisterNetwork;
